import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import { BOOKING_CONSTANTS } from "@constants/Bookings";
import { DateFormats } from "@enums/shared.enum";
import {
  AllowedDurationsMap,
  ResourceType,
  timeMapObj,
} from "@models/services/booking";
import bookingSlice from "@slices/booking.slice";
import { RootState } from "@store/index";
import { policyLateBookingWindowMinutes } from "@utils/booking.utils";

export const getPreviousTimeSlots = (
  data: AllowedDurationsMap | undefined | null,
  time: string
): string[] => {
  if (!data) {
    return [];
  }
  const timeConfig = data[`T_${time}`];

  if (!timeConfig) {
    return [];
  }

  const durations = Object.values(timeConfig)
    .map(item => item.duration)
    .filter((duration): duration is number => duration != null);

  if (!durations.length) {
    return [];
  }

  const maxDuration = Math.max(...durations);

  const [hours, minutes] = time.split(":").map(Number);

  const endMinutes = hours * 60 + minutes;
  const startMinutes = endMinutes - maxDuration + 15;

  const slots: string[] = [];

  for (let current = startMinutes; current < endMinutes; current += 15) {
    if (current < 0) {
      continue;
    }

    const slotHours = Math.floor(current / 60);
    const slotMinutes = current % 60;

    slots.push(
      `${String(slotHours).padStart(2, "0")}:${String(slotMinutes).padStart(
        2,
        "0"
      )}`
    );
  }

  return slots;
};
export const getBlockStartsAndEnds15 = (times: string[]) => {
  const allowed: string[] = [];
  if (times.length === 0) return allowed;

  const sorted = Array.from(new Set(times)).sort();
  allowed.push(sorted[0]);

  for (let i = 0; i < sorted.length - 1; i++) {
    const current = new Date(`2000-01-01T${sorted[i]}:00`);
    const next = new Date(`2000-01-01T${sorted[i + 1]}:00`);

    current.setMinutes(current.getMinutes() + 15);

    if (current < next) {
      allowed.push(current.toTimeString().slice(0, 5));
      allowed.push(sorted[i + 1]);
    }
  }

  const last = new Date(`2000-01-01T${sorted[sorted.length - 1]}:00`);
  last.setMinutes(last.getMinutes() + 15);
  allowed.push(last.toTimeString().slice(0, 5));

  return allowed;
};

export const useBackToBackBooking = () => {
  const dispatch = useDispatch();
  const { bookingEditData, isEditBooking, selectedDate } = useSelector(
    (State: RootState) => State.booking
  );

  function getMissing15MinSlots(
    slotStart: string,
    slotEnd: string,
    availableSlots: string[],
    allowedDurations?: AllowedDurationsMap
  ): string[] {
    const start = new Date(slotStart);
    const end = new Date(slotEnd);

    const availableSet = new Set(availableSlots);
    const missingSlots: string[] = [];

    const current = new Date(start);

    while (current <= end) {
      const hours = String(current.getHours()).padStart(2, "0");
      const minutes = String(current.getMinutes()).padStart(2, "0");

      const time = `${hours}:${minutes}`;

      const isMissingInAvailable = !availableSet.has(time);
      const isMissingInAllowedDurations =
        allowedDurations &&
        Object.keys(allowedDurations).length > 0 &&
        !allowedDurations[`T_${time}`];

      if (isMissingInAvailable || isMissingInAllowedDurations) {
        missingSlots.push(time);
      }

      current.setMinutes(current.getMinutes() + 15);
    }

    return missingSlots;
  }

  function getOccupiedSlots(resource: ResourceType): string[] {
    const isBookingEndAtSameDay =
      resource?.policyInfo?.bookingEndAt &&
      moment(resource.policyInfo.bookingEndAt).isValid() &&
      moment(resource.policyInfo.bookingEndAt).isSame(
        moment(selectedDate),
        "day"
      );

    const missingSlots = getMissing15MinSlots(
      resource?.slotInfo?.slotStart ?? "",
      isBookingEndAtSameDay && resource?.policyInfo?.bookingEndAt
        ? resource.policyInfo.bookingEndAt
        : (resource?.slotInfo?.slotEnd ?? ""),
      resource?.slotInfo?.slots?.availableSlots ?? [],
      resource?.slotInfo?.slots?.allowedDurations
    );

    const allowedDurations = resource?.slotInfo?.slots?.allowedDurations;
    const allowedMissingSlots: string[] = [];
    if (allowedDurations && Object.keys(allowedDurations).length > 0) {
      (resource?.slotInfo?.slots?.availableSlots ?? []).forEach(slot => {
        if (!allowedDurations[`T_${slot}`]) {
          allowedMissingSlots.push(slot);
        }
      });
    }

    return [
      ...new Set([
        ...missingSlots,
        ...allowedMissingSlots,
        ...(resource?.slotInfo?.slots?.occupiedSlots ?? []),
      ]),
    ];
  }

  const isWithinOriginalEditRange = (resourceId: string, timeStr: string) => {
    if (!isEditBooking) return false;

    if (selectedDate !== bookingEditData?.startDate) return false;

    const editResourceId =
      bookingEditData?.resource?.id || bookingEditData?.resourceId;
    if (editResourceId !== resourceId) return false;

    const start = moment(bookingEditData?.startTime, DateFormats.Time);
    const end = moment(bookingEditData?.endTime, DateFormats.Time);
    const slotTime = moment(timeStr, DateFormats.Time);

    if (!start.isValid() || !end.isValid() || !slotTime.isValid()) {
      return false;
    }

    // [start, end]
    return slotTime.isSameOrAfter(start) && slotTime.isSameOrBefore(end);
  };

  function checkBackToBackSlot(
    resource: ResourceType,
    time: string,
    occupiedSlots: string[],
    isAvilableslot: string[],
    isToday: boolean,
    nextTime: string,
    hasPassed: boolean,
    slotStartTime: string,
    isLateBookingAndAvailable: boolean,
    nextSlotTime: string
  ): boolean {
    const nextSlotTimes = nextTimes(time);

    const hasOccupiedNextSlot = nextSlotTimes.some(nextTime =>
      occupiedSlots.includes(nextTime)
    );
    const bookingStartTimes = resource?.policyInfo?.bookingStartTimes ?? ["15"];
    const hasPreviousOccupiedSlot = previousTimes(time, bookingStartTimes).some(
      previousTime => occupiedSlots.includes(previousTime)
    );
    if (
      isLateBookingAndAvailable &&
      (!hasOccupiedNextSlot || !hasPreviousOccupiedSlot)
    ) {
      const isPassed = previousTimes(time, bookingStartTimes).some(
        previousTime => previousTime < nextSlotTime
      );
      const isPassedFlag = !hasPreviousOccupiedSlot && isPassed;
      return isPassedFlag && hasOccupiedNextSlot ? false : true;
    } else if (isLateBookingAndAvailable) {
      return false;
    }

    if (occupiedSlots?.length > 0) {
      const isNotAllowed = hasOccupiedNextSlot || occupiedSlots.includes(time);

      if (
        isNotAllowed ||
        !isValidBookingStartTime(
          time,
          resource?.policyInfo?.bookingStartTimes ?? ["15"]
        )
      ) {
        return false;
      }

      if (occupiedSlots.includes(time)) {
        return false;
      } else if (hasPreviousOccupiedSlot && !hasPassed) {
        if (isWithinOriginalEditRange(resource.id, time)) {
          return false;
        }
        return true;
      } else if (!isToday && slotStartTime && time === slotStartTime) {
        return true;
      } else if (!isToday && isAvilableslot.length === 0) {
        return true;
      } else if (isToday && nextTime === time) {
        return true;
      } else if (
        isToday &&
        getNextStartTime(
          resource?.policyInfo?.bookingStartTimes ?? ["15"],
          0
        ) === time
      ) {
        return true;
      }
    } else {
      if (
        !isValidBookingStartTime(
          time,
          resource?.policyInfo?.bookingStartTimes ?? ["15"]
        )
      ) {
        return false;
      }
      if (isToday && nextTime === time) {
        return true;
      } else if (!isToday && slotStartTime && time === slotStartTime) {
        return true;
      } else if (!isToday && isAvilableslot.length === 0) {
        return true;
      } else if (
        isToday &&
        getNextStartTime(
          resource?.policyInfo?.bookingStartTimes ?? ["15"],
          0
        ) === time
      ) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }
  function nextTimes(time: string): string[] {
    const [hours, minutes] = time.split(":").map(Number);

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);

    const nextDate = new Date(date);
    nextDate.setMinutes(nextDate.getMinutes() + 15);

    // Don't cross to the next day
    if (nextDate.getDate() !== date.getDate()) {
      return [time];
    }

    return [time, nextDate.toTimeString().slice(0, 5)];
  }
  // function getNextTime(time: string, bookingStartTimes: string[]): string {
  //   const [hours, minutes] = time.split(":").map(Number);

  //   const date = new Date();
  //   date.setHours(hours, minutes, 0, 0);

  //   const interval = Math.min(...bookingStartTimes.map(Number));

  //   date.setMinutes(date.getMinutes() + interval);

  //   return date.toTimeString().slice(0, 5);
  // }

  function previousTimes(time: string, bookingStartTimes: string[]): string[] {
    const [hours, minutes] = time.split(":").map(Number);

    const currentMinutes = hours * 60 + minutes;

    const previousTimes = bookingStartTimes
      .map(Number)
      .map(interval => {
        const anchorMinute = interval === 60 ? 0 : interval;

        let hour = hours;

        // If current time is at/before the anchor,
        // use the previous hour.
        if (anchorMinute >= minutes) {
          hour--;
        }

        if (hour < 0) {
          hour = 23;
        }

        const totalMinutes = hour * 60 + anchorMinute;

        return {
          diff: (currentMinutes - totalMinutes + 1440) % 1440,
          time: `${String(hour).padStart(2, "0")}:${String(
            anchorMinute
          ).padStart(2, "0")}`,
        };
      })
      .sort((a, b) => a.diff - b.diff);

    return previousTimes.length ? [previousTimes[0].time] : [];
  }

  const isValidBookingStartTime = (
    time: string,
    bookingStartTimes: string[]
  ): boolean => {
    const [, minutes] = time.split(":").map(Number);

    return bookingStartTimes.some(interval => {
      const value = Number(interval);

      if (value === 60) {
        return minutes === 0;
      }

      return minutes === value;
    });
  };
  const getNextStartTime = (
    bookingStartTimes: string[],
    lateBookingWindow = 0
  ): string => {
    const now = moment()
      .subtract(lateBookingWindow, "minutes")
      .startOf("minute");

    const intervals = [...new Set(bookingStartTimes.map(Number))];

    const candidates: moment.Moment[] = [];

    for (const interval of intervals) {
      const minute = interval === 60 ? 0 : interval;

      const candidate = now.clone().minute(minute).second(0).millisecond(0);

      // 60 means the next :00 when current time is already past :00.
      if (interval === 60 && candidate.isSameOrBefore(now)) {
        candidate.add(1, "hour");
      } else if (candidate.isBefore(now)) {
        candidate.add(1, "hour");
      }

      candidates.push(candidate);
    }

    const nextCandidate = candidates
      .filter(candidate => candidate.isSameOrAfter(now))
      .sort((a, b) => a.valueOf() - b.valueOf())[0];

    return nextCandidate?.format("HH:mm") ?? "";
  };
  const isAvailableTimeCalender = (
    resource: ResourceType,
    time: string,
    isAvilableslot: string[] = []
  ) => {
    const isToday = selectedDate === moment().format(DateFormats.YMD);
    const lateBookingWindow = policyLateBookingWindowMinutes(
      resource?.policyInfo
    );
    const slotStartTime = resource?.slotInfo?.slotStart
      ? moment(resource.slotInfo.slotStart)
          .subtract(isToday ? lateBookingWindow : 0, "minutes")
          .format("HH:mm")
      : "";
    let nextTime = getNextStartTime(
      resource?.policyInfo?.bookingStartTimes ?? ["15"],
      lateBookingWindow
    );
    const nextSlotTime = nextTime;
    if (slotStartTime && nextTime < slotStartTime) {
      nextTime = slotStartTime;
    }

    const slotDateTime = moment(`${selectedDate}T${time}`, DateFormats.YMDTime);
    let hasPassed = slotDateTime.isBefore(moment());

    const occupiedSlots = getOccupiedSlots(resource);

    const isLateBookingAndAvailable =
      isToday &&
      lateBookingWindow > 0 &&
      slotDateTime.isBefore(moment()) &&
      slotDateTime
        .clone()
        .add(lateBookingWindow, "minutes")
        .isSameOrAfter(moment()) &&
      !occupiedSlots.includes(time) &&
      isValidBookingStartTime(
        time,
        resource?.policyInfo?.bookingStartTimes ?? ["15"]
      );

    if (slotDateTime.isBefore(moment())) {
      const lateBookingLimit = slotDateTime
        .clone()
        .add(lateBookingWindow, "minutes");
      hasPassed = lateBookingLimit.isBefore(moment());
    }

    return checkBackToBackSlot(
      resource,
      time,
      occupiedSlots,
      isAvilableslot,
      isToday,
      nextTime,
      hasPassed,
      slotStartTime,
      isLateBookingAndAvailable,
      nextSlotTime
    );
  };

  const isAvailableTime = (
    resource: ResourceType,
    time: string,
    isAvilableslot: string[] = []
  ) => {
    const isToday = selectedDate === moment().format(DateFormats.YMD);
    const lateBookingWindow = policyLateBookingWindowMinutes(
      resource?.policyInfo
    );
    const slotStartTime = resource?.slotInfo?.slotStart
      ? moment(resource.slotInfo.slotStart)
          .subtract(isToday ? lateBookingWindow : 0, "minutes")
          .format("HH:mm")
      : "";
    let nextTime = getNextStartTime(
      resource?.policyInfo?.bookingStartTimes ?? ["15"],
      lateBookingWindow
    );
    const nextSlotTime = nextTime;
    if (slotStartTime && nextTime < slotStartTime) {
      nextTime = slotStartTime;
    }

    const slotDateTime = moment(`${selectedDate}T${time}`, DateFormats.YMDTime);
    let hasPassed = slotDateTime.isBefore(moment());

    const occupiedSlots = getOccupiedSlots(resource);

    const isLateBookingAndAvailable =
      isToday &&
      lateBookingWindow > 0 &&
      slotDateTime.isBefore(moment()) &&
      slotDateTime
        .clone()
        .add(lateBookingWindow, "minutes")
        .isSameOrAfter(moment()) &&
      !occupiedSlots.includes(time) &&
      isValidBookingStartTime(
        time,
        resource?.policyInfo?.bookingStartTimes ?? ["15"]
      );

    if (slotDateTime.isBefore(moment())) {
      const lateBookingLimit = slotDateTime
        .clone()
        .add(lateBookingWindow, "minutes");
      hasPassed = lateBookingLimit.isBefore(moment());
    }

    return checkBackToBackSlot(
      resource,
      time,
      occupiedSlots,
      isAvilableslot,
      isToday,
      nextTime,
      hasPassed,
      slotStartTime,
      isLateBookingAndAvailable,
      nextSlotTime
    );
  };

  const isLateBookingAndAvailableSlot = (
    resource: ResourceType,
    time: string
  ): boolean => {
    const isToday = selectedDate === moment().format(DateFormats.YMD);
    const lateBookingWindow = policyLateBookingWindowMinutes(
      resource?.policyInfo
    );
    const slotDateTime = moment(`${selectedDate}T${time}`, DateFormats.YMDTime);

    if (!isToday || lateBookingWindow <= 0) {
      return false;
    }

    if (!slotDateTime.isBefore(moment())) {
      return false;
    }

    const occupiedSlots = getOccupiedSlots(resource);

    const isWithinLateBooking = slotDateTime
      .clone()
      .add(lateBookingWindow, "minutes")
      .isSameOrAfter(moment());

    return (
      isWithinLateBooking &&
      !occupiedSlots.includes(time) &&
      isValidBookingStartTime(
        time,
        resource?.policyInfo?.bookingStartTimes ?? ["15"]
      )
    );
  };

  const setTimeSlotSelectionBackToBack = (
    applicableTimeSlots: string[],
    slotData: {
      availableSlots: string[];
      occupiedSlots: string[];
      unavailableSlots: string[];
    },
    allowedTimeSlots: string[],
    resources: ResourceType[]
  ): Array<timeMapObj[]> => {
    const groupedByHour: Record<string, timeMapObj[]> = {};
    const resourceSlotsMap: Record<string, string[]> = {};
    resources.forEach(r => {
      resourceSlotsMap[r.id] = [];
    });

    applicableTimeSlots.forEach(time => {
      const [hourStr, minuteStr] = time.split(":");
      if (
        (allowedTimeSlots.includes(minuteStr) ||
          (minuteStr === "00" && allowedTimeSlots.includes("60"))) &&
        slotData.availableSlots.includes(time)
      ) {
        let allowCount = 0;

        for (const resource of resources) {
          const slotDateTime = moment(
            `${selectedDate}T${time}`,
            DateFormats.YMDTime
          );
          if (slotDateTime.isBefore(moment())) {
            const lateBookingWindow = policyLateBookingWindowMinutes(
              resource?.policyInfo
            );
            const lateBookingLimit = slotDateTime
              .clone()
              .add(lateBookingWindow, "minutes");
            if (moment().isAfter(lateBookingLimit)) {
              continue;
            }
          }

          const allowed = resource.slotInfo?.slots?.allowedDurations;
          const key = `T_${time}`;

          const durations =
            allowed?.[key] && typeof allowed[key] === "object"
              ? Object.keys(allowed[key]).map(k =>
                  parseInt(k.replace("T_", ""), 10)
                )
              : [];

          if (durations.length === 0) continue;

          const minDuration = Math.min(...durations);
          const requiredSlots = minDuration / BOOKING_CONSTANTS.WIDTH_PER_SLOT;

          let hour = Number(hourStr);
          let minute = Number(minuteStr);
          let allSlotsFree = true;
          const isAvailableSlot = resourceSlotsMap[resource.id];
          for (let i = 0; i < requiredSlots; i++) {
            const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

            if (
              !slotData.availableSlots.includes(timeStr) ||
              (slotData.occupiedSlots.includes(timeStr) &&
                !isWithinOriginalEditRange(resource.id, timeStr))
            ) {
              allSlotsFree = false;
              break;
            }

            minute += BOOKING_CONSTANTS.WIDTH_PER_SLOT;
            if (minute >= BOOKING_CONSTANTS.MINS_IN_HOUR) {
              hour += Math.floor(minute / BOOKING_CONSTANTS.MINS_IN_HOUR);
              minute %= BOOKING_CONSTANTS.MINS_IN_HOUR;
            }
          }
          const avilableTime =
            isEditBooking && allSlotsFree && time === bookingEditData?.startTime
              ? true
              : isAvailableTime(resource, time, isAvailableSlot);

          if (allSlotsFree && avilableTime) {
            isAvailableSlot.push(time);
            allowCount++;
          }
        }
        const slot: timeMapObj = { disabled: allowCount === 0, time };

        if (!groupedByHour[hourStr]) groupedByHour[hourStr] = [];

        groupedByHour[hourStr].push(slot);
      }
    });

    const backToBackBookingSlotsList = Object.entries(resourceSlotsMap).map(
      ([resourceId, validAvailableSlots]) => ({
        resourceId,
        validAvailableSlots,
      })
    );
    dispatch(
      bookingSlice.actions.setBackToBackBookingSlots(backToBackBookingSlotsList)
    );

    return Object.keys(groupedByHour)
      .sort((a, b) => Number(a) - Number(b))
      .map(hour => groupedByHour[hour]);
  };
  return {
    getNextStartTime,
    getOccupiedSlots,
    isAvailableTime,
    isAvailableTimeCalender,
    isLateBookingAndAvailableSlot,
    isValidBookingStartTime,
    nextTimes,
    previousTimes,
    setTimeSlotSelectionBackToBack,
  };
};
